// Decision steps and workflow variables (docs/plans/2026-10-02-beslisstap.md,
// contract docs/plans/2026-10-02-beslisstap-contract.md).
//
// A decision step (`WorkflowState.kind === 'decision'`) asks one question
// with a fixed set of answers. It runs without a sandbox (the worker's
// no-sandbox chat runners), reads only, and ends with the `decide` tool.
// Its answer is stored as a workflow variable on the task, readable by every
// later step of that task.

import type { ModelTier } from './workflow.js'

/** One workflow variable of a task (`tasks.workflow_vars[name]`). */
export interface WorkflowVariable {
  value: string
  /** Why this value: the decision's `reason`, or the person's note on an override. */
  reason?: string | null
  /** The step that set it. */
  source: {
    kind: 'decision' | 'option' | 'override'
    workflowKey: string
    state: string
    runId?: string | null
    actorId?: string | null
  }
  /** ISO timestamp. */
  setAt: string
}

/** `tasks.workflow_vars`: variable name → variable. */
export type WorkflowVariables = Record<string, WorkflowVariable>

/** `runs.kind`. */
export type RunKind = 'agent' | 'decision'

/** `runs.decision`: what a decision run answered. */
export interface DecisionOutcome {
  /** The chosen option's `value` — always a valid one (the default when `fallback`). */
  choice: string
  reason: string | null
  /** True when the default was used (no/invalid answer, timeout, budget, failure). */
  fallback: boolean
  /** What the agent literally answered when it was invalid, for the timeline. */
  rawChoice?: string | null
}

/** The `decision.run` queue's job payload. */
export interface DecisionRunJob {
  runId: string
  taskId: string
  workspaceId: string
}

export const DECISION_RUN_QUEUE = 'decision.run'
export const DECISION_RUN_QUEUE_OPTIONS = {
  retryLimit: 0,
  expireInSeconds: 5 * 60,
} as const

/** Fixed ceiling of one decision run (not configurable per role: a decision is light by definition). */
export const DECISION_BUDGET = {
  maxModelCalls: 12,
  maxToolCalls: 20,
  maxModelTokens: 60_000,
  maxDurationMs: 90_000,
} as const

/** How often the worker polls whether a decision run was cancelled. */
export const DECISION_CANCEL_POLL_MS = 2_000

/** `GET /internal/decisions/:runId` — everything the worker needs to run one decision. */
export interface DecisionRunContext {
  runId: string
  taskId: string
  workspaceId: string
  projectId: string | null
  /** Same connection fields as a chat turn's context (worker/src/chat/types.ts `ChatTurnContext`). */
  credentialId: string
  credentialProjectId: string | null
  runner: 'claude-cli' | 'pi'
  connectionKind: 'claude_cli' | 'codex_cli' | 'openai_compatible' | 'pi_login'
  endpoint: string | null
  piProvider?: string | null
  /** The decision agent's own model (a task's model tier never applies to the decision itself). */
  model: string | null
  networkAllowlist: { domain: string; ports: number[] }[]
  /** The fully rendered system prompt + user message (API composes them; `{{vars.x}}` already substituted). */
  prompt: { system: string; user: string }
  options: { value: string; description: string | null }[]
  defaultOption: string
  /** Read-only tools this decision may use (subset of DECISION_TOOL_NAMES minus `decide`, which is always there). */
  tools: DecisionToolName[]
  budget: typeof DECISION_BUDGET
}

/** Every tool a decision run can be offered. `decide` is always offered. */
export const DECISION_TOOL_NAMES = ['get_task', 'kb_search', 'kb_read', 'repo_tree', 'repo_read', 'repo_search', 'decide'] as const
export type DecisionToolName = (typeof DECISION_TOOL_NAMES)[number]

/** `POST /internal/decisions/:runId/tools/:name` body/response — every read-only tool except `decide` is executed by the API. */
export interface DecisionToolCall {
  input: Record<string, unknown>
}
export interface DecisionToolResult {
  ok: boolean
  /** Plain text handed back to the model. */
  output: string
}

/** `POST /internal/decisions/:runId/events` body: one timeline entry (a tool call, a model message, a notice). */
export interface DecisionRunEvent {
  type: 'tool_call' | 'message' | 'notice'
  payload: Record<string, unknown>
}

/** `POST /internal/decisions/:runId/finish` body. */
export interface DecisionRunFinish {
  status: 'completed' | 'failed' | 'timed_out' | 'cancelled'
  /** What `decide` received, unvalidated (the API validates and applies the default). Null when `decide` was never called. */
  decision: { choice: string; reason: string | null } | null
  usage: { modelCalls: number; toolCalls: number; inputTokens: number; outputTokens: number; costCents?: number | null }
  error?: string | null
}

/** `POST /tasks/:id/decision-override` body. */
export interface DecisionOverrideRequest {
  /** State name of the decision step whose answer is replaced (the latest visit counts). */
  state: string
  option: string
  note?: string | null
}

/** Model tier per connection, stored in `credentials.scope.modelTiers`. */
export type ConnectionModelTiers = Partial<Record<ModelTier, string>>

/** Default tier → model for a Claude Code connection (aliases from executor-models.service.ts). */
export const CLAUDE_CLI_DEFAULT_MODEL_TIERS: Required<ConnectionModelTiers> = {
  light: 'haiku',
  standard: 'sonnet',
  heavy: 'opus',
}
